(ns ui.components.button.view
  (:require
   [frontend.logging :as logging]
   [ui.color :as c]
   [ui.components.button.zapkit-styles :as styles]
   [ui.components.radix-ui.tooltip.view :as tooltip]
   [ui.util :as uu]))


(def default-props
  {:variant   :text
   :color     ::c/primary
   :size      :medium
   :component :button
   :disabled? false})

(defn button
  [{:keys [] :as props} & children]
  (let [children (if-not (map? props) (cons props children) children)
        props (if (map? props)
                (merge default-props props)
                default-props)
        {:keys [id on-click start-icon end-icon]}
        props]
    ;; TODO: Handle active-input blur/flush sequencing in the shared button layer (e.g. configurable
    ;; pre-click behavior) so feature views don't need local blur/setTimeout workarounds.
    [:button
     {:on-click (fn [e]
                  (when (fn? on-click)
                    (on-click e)))}
     [:span
      (when start-icon
        start-icon)

      (->> children
           (map-indexed (fn [index el]
                          ^{:key (str id "-" index)}
                          [:span el])))

      (when end-icon
        end-icon)]]))

;; TAILWIND BUTTON COMPONENTS
;; if you need to apply some styles from outside the component (eg. margins etc...), please use the class attribute
(defn- tooltip-button
  [{:keys [on-click disabled? tooltip] :as props} & children]
  (if-not tooltip
    (into [:button
           (merge (-> props
                      (dissoc :onClick :tooltip :disabled? :on-click :dom-id)
                      (assoc
                        :on-click (fn [e]
                                    (when (and (not disabled?)
                                               (fn? on-click))
                                      (on-click e))
                                    #_(when (fn? onClick)
                                        ;; tooltip forwardRef cb
                                        (onClick e)))
                        :disabled (boolean disabled?)))
                  (when-let [dom-id (:dom-id props)]
                    {:id dom-id}))]
          children)
    [tooltip/as-child
     {}
     (fn [f-props]
       (apply tooltip-button
              (concat [(-> props
                           (dissoc :tooltip)
                           (merge f-props))]
                      children)))
     tooltip]))

(defn- base-button
  [{:keys [class] :as props} & children]
  (when-not (map? props)
    (logging/debug "Missing button props"))
  (apply tooltip-button
         (concat [(assoc props
                    :class (uu/cm "m-0 outline-none border border-solid leading-snug font-muli font-semibold text-md rounded"
                                  "underline-offset-4 whitespace-nowrap flex gap-2 justify-center items-center"
                                  "*:flex *:gap-2 *:justify-center *:items-center"
                                  "cursor-pointer disabled:cursor-not-allowed select-none"
                                  class))]
                 children)))

(def tooltip-disabled-open-classes
  "border-transparent border-0 py-3 text-disabled-regular-text
  m-0 outline-none border border-solid leading-5 font-muli font-semibold text-md rounded
  underline-offset-4 whitespace-nowrap flex gap-2 justify-center items-center
  *:flex *:gap-2 *:justify-center *:items-center cursor-not-allowed")

(defn primary-solid
  [{:keys [class] :as props} & children]
  (apply base-button
         (concat
          [(assoc props
             :class (uu/cm "px-5 py-3
           text-primary-regular-text hover:text-primary-dark-text disabled:text-disabled-regular-text
           border border-primary-regular-border hover:border-primary-gloomy-border disabled:border-disabled-regular-border
           bg-primary-regular hover:bg-primary-gloomy disabled:bg-disabled-regular
           focus:scale-[0.97]"
                           class))]
          children)))

(defn danger-solid
  [{:keys [class] :as props} & children]
  (apply base-button
         (concat
          [(assoc props
             :class (uu/cm "px-5 py-3
         text-danger-regular-text disabled:text-disabled-regular-text
         border border-danger-regular-border hover:border-danger-gloomy-border disabled:border-disabled-regular-border
         bg-danger-regular hover:bg-danger-gloomy disabled:bg-disabled-regular
         focus:scale-[0.97]"
                           class))]
          children)))

(defn warning-solid
  [{:keys [class] :as props} & children]
  (apply base-button
         (concat
          [(assoc props
             :class (uu/cm "px-5 py-3
          text-warning-regular-text disabled:text-disabled-regular-text
          border border-warning-regular-border hover:border-warning-gloomy-border disabled:border-disabled-regular-border
          bg-warning-regular hover:bg-warning-gloomy disabled:bg-disabled-regular
          focus:scale-[0.97]"
                           class))]
          children)))

(defn sm-primary-solid
  [{:keys [class] :as props} & children]
  (apply base-button
         (concat
          [(assoc props
             :class (uu/cm "px-3 py-1.5
         text-sm text-primary-regular-text hover:text-primary-regular-dark-text disabled:text-disabled-regular-text
         border border-primary-regular-border hover:border-primary-gloomy-border disabled:border-disabled-regular-border
         bg-primary-regular hover:bg-primary-gloomy disabled:bg-disabled-regular"
                           class))]
          children)))

(defn sm-danger-solid
  [{:keys [class] :as props} & children]
  (apply base-button
         (concat
          [(assoc props
             :class (uu/cm "px-3 py-2
         text-sm text-danger-regular-text disabled:text-disabled-regular-text
         border border-danger-regular-border hover:border-danger-gloomy-border disabled:border-disabled-regular-border
         bg-danger-regular hover:bg-danger-gloomy disabled:bg-disabled-regular"
                           class))]
          children)))

(defn sm-warning-solid
  [{:keys [class] :as props} & children]
  (apply base-button
         (concat
          [(assoc props
             :class (uu/cm "px-2 py-1.5
          text-sm text-warning-regular-text disabled:text-disabled-regular-text
          border border-warning-regular-border hover:border-warning-gloomy-border disabled:border-disabled-regular-border
          bg-warning-regular hover:bg-warning-gloomy disabled:bg-disabled-regular
          focus:scale-[0.97]"
                           class))]
          children)))

(defn sm-secondary-solid
  [{:keys [class] :as props} & children]
  (apply base-button
         (concat
          [(assoc props
             :class
             (uu/cm
              "px-2 py-1.5
          text-sm text-secondary-light-text hover:text-secondary-light-text-accent disabled:text-disabled-regular-text
          border border-secondary-light-border disabled:border-disabled-regular-border
          bg-secondary-light disabled:bg-regular
          focus:scale-[0.97]"
              class))]
          children)))

(defn secondary-hollow
  [{:keys [class] :as props} & children]
  (apply base-button
         (concat
          [(assoc props
             :class (uu/cm "px-5 py-3
          text-secondary-light-text hover:text-secondary-light-accent disabled:text-disabled-regular-text
          border border-secondary-light-border disabled:border-disabled-regular-border
          hover:bg-secondary-light disabled:bg-regular
          focus:scale-[0.97]"
                           class))]
          children)))

(defn secondary-solid
  [{:keys [class] :as props} & children]
  (apply base-button
         (concat
          [(assoc props
             :class (uu/cm "px-5 py-3
          text-secondary-light-text hover:text-secondary-light-accent disabled:text-disabled-regular-text
          border border-secondary-light-border disabled:border-disabled-regular-border
          bg-secondary-light hover:bg-secondary-light-accent disabled:bg-regular
          focus:scale-[0.97]"
                           class))]
          children)))

(defn sm-secondary-hollow
  [{:keys [class] :as props} & children]
  (apply base-button
         (concat
          [(assoc props
             :class (uu/cm "px-4 py-1.5
          text-sm text-secondary-light-text hover:text-secondary-light-text disabled:text-disabled-regular-text
          border border-secondary-light-border disabled:border-disabled-regular-border
          hover:bg-secondary-light disabled:bg-regular
          focus:scale-[0.97]"
                           class))]
          children)))

(defn primary-open
  [{:keys [class] :as props} & children]
  (apply base-button
         (concat
          [(assoc props
             :class (uu/cm "py-3
          text-primary-light-text-accent hover:underline disabled:text-disabled-regular-text
          border-transparent border-0"
                           class))]
          children)))

(defn secondary-open
  [{:keys [class] :as props} & children]
  (apply base-button
         (concat
          [(assoc props
             :class (uu/cm "py-3
          text-secondary-light-text hover:text-secondary-light-text-accent disabled:text-disabled-regular-text
          border-0"
                           class))]
          children)))

(defn helper-button
  [{:keys [class] :as props} & children]
  (apply base-button
         (concat
          [(assoc props
             :class (uu/cm class "flex items-center gap-2 px-2 py-1 text-xs font-medium text-secondary-light-text rounded bg-complement-regular hover:bg-complement-gloomy
                                                 disabled:text-disabled-light-text disabled:bg-disabled-light"))]
          children)))

(defn square-base-button
  [{:keys [class] :as props} & children]
  (apply tooltip-button
         (concat [(assoc props
                    :class (uu/cm "mb-0 outline-none border border-solid leading-5 rounded"
                                  "muli font-semibold"
                                  "flex flex-row gap-2 *:flex *:gap-2 *:justify-center *:items-center justify-center items-center"
                                  "w-6 h-6 text-sm focus-scale-95"
                                  class))]
                 children)))

(defn square-primary-solid
  [{:keys [class] :as props} & children]
  (apply square-base-button
         (concat [(assoc props :class
                               (uu/cm "text-primary-regular-text hover:text-primary-dark-text disabled:text-disabled-regular-text
         border border-primary-regular-border hover:border-primary-gloomy-border disabled:border-disabled-regular-border
         bg-primary-regular hover:bg-primary-gloomy disabled:bg-disabled-regular"
                                      class))] children)))

(defn square-danger-solid
  [{:keys [class] :as props} & children]
  (apply square-base-button
         (concat [(assoc props
                    :class (uu/cm "text-danger-regular-text disabled:text-disabled-regular-text
           border border-danger-regular-border hover:border-danger-gloomy-border disabled:border-disabled-regular-border
           bg-danger-regular hover:bg-danger-gloomy disabled:bg-disabled-regular
           focus:scale-[0.97]" class))]
                 children)))

(defn square-danger-hollow
  [{:keys [class] :as props} & children]
  (apply square-base-button
         (concat [(assoc props
                    :class (uu/cm "text-danger-regular disabled:text-disabled-regular
           border border-danger-regular-border hover:border-danger-gloomy-border disabled:border-disabled-regular-border
           disabled:bg-disabled-regular
           focus:scale-[0.97]" class))]
                 children)))

(defn square-secondary-hollow
  [{:keys [class] :as props} & children]
  (apply square-base-button
         (concat [(assoc props :class (uu/cm
                                       "text-secondary-light-text hover:text-secondary-light-text-accent disabled:text-disabled-regular-text
                                        border border-secondary-light-border hover:border-secondary-light-border-accent disabled:border-disabled-regular-border
                                        hover:bg-transparent disabled:bg-regular
                                        focus:scale-[0.97]"
                                       class))]
                 children)))

(defn square-secondary-open
  [{:keys [class] :as props} & children]
  (apply square-base-button
         (concat [(assoc props :class
                               (uu/cm "text-secondary-light-text hover:text-secondary-light-text-accent disabled:text-disabled-regular-text border-0"
                                      class))]
                 children)))


(defn link-style
  [cls]
  (uu/cm "text-base-light-text-contrast underline cursor-pointer"
         cls))

;; trying out styles from zapkit-components repo:
;; e.g. [button/component {:variant :secondary :size :md ...} "..."]
;;      corresponds to [button/secondary-hollow ...]
(defn component
  [{:keys [variant size class] :as props} & children]
  (let [variant (or variant :primary)
        styles styles/button
        size (or size :md)]
    (apply tooltip-button
           (concat [(assoc props
                           :class (uu/cm (:base styles)
                                         (get-in styles [:size size])
                                         (get-in styles [:variant variant])
                                         class))]
                   children))))
